export const activeLocales = ["en", "ar"] as const;
export const preparedLocales = ["zh-CN", "ru", "de", "nl-NL"] as const;
export type ActiveLocale = (typeof activeLocales)[number];
export type PreparedLocale = (typeof preparedLocales)[number];
export type Locale = ActiveLocale | PreparedLocale;

export const localeConfig: Record<Locale, {
  label: string;
  nativeLabel: string;
  dir: "ltr" | "rtl";
  status: "active" | "prepared";
  pathPrefix: string;
  reviewRequired: boolean;
}> = {
  en: { label: "English", nativeLabel: "English", dir: "ltr", status: "active", pathPrefix: "", reviewRequired: false },
  ar: { label: "Arabic", nativeLabel: "العربية", dir: "rtl", status: "active", pathPrefix: "", reviewRequired: false },
  "zh-CN": { label: "Simplified Chinese", nativeLabel: "简体中文", dir: "ltr", status: "prepared", pathPrefix: "/zh-cn", reviewRequired: true },
  ru: { label: "Russian", nativeLabel: "Русский", dir: "ltr", status: "prepared", pathPrefix: "/ru", reviewRequired: true },
  de: { label: "German", nativeLabel: "Deutsch", dir: "ltr", status: "prepared", pathPrefix: "/de", reviewRequired: true },
  "nl-NL": { label: "Dutch", nativeLabel: "Nederlands", dir: "ltr", status: "prepared", pathPrefix: "/nl", reviewRequired: true },
};

export const translations = {
  en: {
    // Nav / Header
    "Explore": "Explore",
    "Companies": "Companies",
    "Products": "Products",
    "Services": "Services",
    "For Businesses": "For Businesses",
    "About": "About",
    "List Your Business": "List Your Business",
    "Home": "Home",
    "Deals": "Deals",
    "FAQ": "FAQ",
    "Dubai guide": "Dubai guide",
    "Toggle language": "Toggle language",
    "English": "English",
    "All Listings": "All Listings",
    "No listings available for this merchant yet.": "No listings available for this merchant yet.",
    "Opening Hours": "Opening Hours",
    "Service Methods": "Service Methods",
    "USDT Networks": "USDT Networks",
    "Notified": "Notified",
    "Webhook Pending": "Webhook Pending",

    // Hero
    "Discover USDT-Friendly Businesses in Dubai": "Discover USDT-Friendly Businesses in Dubai",
    "Discover where to spend USDT in Dubai": "Discover Where to Spend USDT in Dubai",
    "Browse products and services from independent Dubai merchants that state they accept USDT. Contact each merchant directly to confirm availability, pricing, and payment terms.": "Browse products and services from independent Dubai merchants that state they accept USDT. Contact each merchant directly to confirm availability, pricing, and payment terms.",
    "Search products, services, or businesses": "Search products, services, or businesses",
    "All Areas": "All Areas",
    "Explore Dubai": "Explore Dubai",
    "Direct Peer-to-Peer": "Direct Peer-to-Peer",
    "No Wallets Connected": "No Wallets Connected",
    "Independent Merchants": "Independent Merchants",
    "USDT-friendly companies": "USDT-friendly companies",
    "Companies & Merchants": "Companies & Merchants",
    "Browse companies and merchants, then explore the products and services they list.": "Browse companies and merchants, then explore the products and services they list.",
    "Unable to load companies": "Unable to load companies",
    "Please try again in a moment.": "Please try again in a moment.",
    "No companies listed yet": "No companies listed yet",
    "Check back soon for more USDT-friendly merchants.": "Check back soon for more USDT-friendly merchants.",
    "Explore listings": "Explore listings",

    // Categories section
    "Categories": "Categories",
    "View all": "View all",
    "listings": "listings",

    // Featured listings
    "Featured": "Featured",
    "Featured Listings": "Featured Listings",
    "Browse all featured": "Browse all featured",
    "USDT Accepted": "USDT Accepted",
    "View Details": "View Details",
    "Contact for price": "Contact for price",

    // How It Works
    "How It Works": "How It Works",
    "Simple steps to find USDT-friendly businesses in Dubai.": "Simple steps to find USDT-friendly businesses in Dubai.",
    "Discover": "Discover",
    "Find listed products and services from Dubai merchants.": "Find listed products and services from Dubai merchants.",
    "Contact the Merchant": "Contact the Merchant",
    "Contact directly via WhatsApp, phone, or email to confirm details and availability.": "Contact directly via WhatsApp, phone, or email to confirm details and availability.",
    "Arrange Payment Directly": "Arrange Payment Directly",
    "Agree with the merchant on price, transfer network, and payment terms. Souq Crypto does not receive any funds.": "Agree with the merchant on price, transfer network, and payment terms. Souq Crypto does not receive any funds.",

    // Regional Expansion Teaser
    "OUR EXPANSION ROADMAP": "OUR EXPANSION ROADMAP",
    "Dubai First. Three Major Cities Next.": "Dubai First. Three Major Cities Next.",
    "Dubai is our first-year launch market. As our merchant community grows, Souq Crypto plans to expand into three major regional cities. The next destinations will be revealed as partnerships are confirmed.": "Dubai is our first-year launch market. As our merchant community grows, Souq Crypto plans to expand into three major regional cities. The next destinations will be revealed as partnerships are confirmed.",
    "YEAR 1 — LAUNCH MARKET": "YEAR 1 — LAUNCH MARKET",
    "Dubai": "Dubai",
    "Building Dubai's focused directory for discovering USDT-friendly products, services, and businesses.": "Building Dubai's focused directory for discovering USDT-friendly products, services, and businesses.",
    "NEXT CITY": "NEXT CITY",
    "TO BE REVEALED": "TO BE REVEALED",
    "A major regional market is planned as part of the next expansion phase.": "A major regional market is planned as part of the next expansion phase.",
    "Follow our journey. The next cities will be announced soon.": "Follow our journey. The next cities will be announced soon.",

    // Homepage CTA
    "Are you a Dubai merchant?": "Are you a Dubai merchant?",
    "List your Dubai business on Souq Crypto and connect with customers searching for USDT-friendly merchants.": "List your Dubai business on Souq Crypto and connect with customers searching for USDT-friendly merchants.",
    "List Your Business Now": "List Your Business Now",
    "Learn More": "Learn More",

    // Disclaimer
    "Souq Crypto is an independent business directory. We do not exchange, receive, hold, transfer, or process cryptocurrency or customer funds. Customers and merchants arrange transactions independently and are responsible for confirming all payment terms.": "Souq Crypto is an independent business directory. We do not exchange, receive, hold, transfer, or process cryptocurrency or customer funds. Customers and merchants arrange transactions independently and are responsible for confirming all payment terms.",
    "Disclaimer: Souq Crypto is an independent business directory.": "Disclaimer: Souq Crypto is an independent business directory.",
    "Souq Crypto does not hold or process any funds; all payments are between buyer and merchant.": "Souq Crypto does not hold or process any funds; all payments are between buyer and merchant.",

    // For Businesses page
    "Reach Customers Who Prefer USDT": "Reach Customers Who Prefer USDT",
    "List your Dubai business on Souq Crypto and connect with customers searching for products and services from USDT-friendly merchants.": "List your Dubai business on Souq Crypto and connect with customers searching for products and services from USDT-friendly merchants.",
    "Apply to List Your Business": "Apply to List Your Business",
    "Why list on Souq Crypto?": "Why list on Souq Crypto?",
    "Connect with customers searching for USDT-friendly businesses in Dubai.": "Connect with customers searching for USDT-friendly businesses in Dubai.",
    "Direct Customer Relationships": "Direct Customer Relationships",
    "Buyers contact you directly via WhatsApp, phone, or email.": "Buyers contact you directly via WhatsApp, phone, or email.",
    "No Transaction Fees": "No Transaction Fees",
    "Category & Keyword Search": "Category & Keyword Search",
    "Buyers filter by category, area, product type, and keywords to find exactly what they need — surfacing your listings to the right people.": "Buyers filter by category, area, product type, and keywords to find exactly what they need — surfacing your listings to the right people.",
    "We do not process payments or take transaction fees. All funds go directly between buyer and merchant.": "We do not process payments or take transaction fees. All funds go directly between buyer and merchant.",
    "Increased Visibility": "Increased Visibility",
    "Stand out in a focused directory for USDT-friendly businesses in Dubai.": "Stand out in a focused directory for USDT-friendly businesses in Dubai.",
    "Listing Options": "Listing Options",
    "Choose the option that fits your business.": "Choose the option that fits your business.",
    "Basic Listing": "Basic Listing",
    "Free basic listings are available during the launch period.": "Free basic listings are available during the launch period.",
    "Standard merchant profile": "Standard merchant profile",
    "Up to 5 product/service listings": "Up to 5 product/service listings",
    "Direct WhatsApp & Email leads": "Direct WhatsApp & Email leads",
    "Standard search visibility": "Standard search visibility",
    "Get Listed Free": "Get Listed Free",
    "Featured Partner": "Featured Partner",
    "Maximum visibility for established businesses.": "Maximum visibility for established businesses.",
    "Everything in Basic": "Everything in Basic",
    "Unlimited listings": "Unlimited listings",
    "Homepage placement": "Homepage placement",
    "Featured badge on all listings": "Featured badge on all listings",
    "Priority in search results": "Priority in search results",
    "Apply for Early Access": "Apply for Early Access",
    "No payment required today. We will contact you.": "No payment required today. We will contact you.",
    "How listing works": "How listing works",
    "Apply": "Apply",
    "Fill out the application form with your business details.": "Fill out the application form with your business details.",
    "Review": "Review",
    "Our team reviews your application and may be in touch with questions.": "Our team reviews your application and may be in touch with questions.",
    "Go Live": "Go Live",
    "Your listings go live. Buyers contact you directly to arrange USDT payment.": "Your listings go live. Buyers contact you directly to arrange USDT payment.",
    "Recommended": "Recommended",
    "during launch": "during launch",

    // Explore page
    "Filters": "Filters",
    "Search": "Search",
    "Keywords...": "Keywords...",
    "Type": "Type",
    "All": "All",
    "Category": "Category",
    "All Categories": "All Categories",
    "Dubai Area": "Dubai Area",
    "Premium Only": "Premium Only",
    "Clear Filters": "Clear Filters",
    "Showing results": "Showing",
    "Showing": "Showing",
    "results": "results",
    "Newest": "Newest",
    "Name (A-Z)": "Name (A-Z)",
    "Sort by": "Sort by",
    "No results found": "No results found",
    "Try adjusting your filters to find what you're looking for.": "Try adjusting your filters to find what you're looking for.",
    "Ask price": "Ask price",
    "How do you serve customers?": "How do you serve customers?",
    "Select at least one service method": "Select at least one service method",
    "All service methods": "All service methods",
    "Service methods": "Service methods",
    "Edit service methods": "Edit service methods",
    "Service at Merchant Location": "Service at Merchant Location",
    "Service at Customer Location / Home Service": "Service at Customer Location / Home Service",
    "Physical Goods": "Physical Goods",
    "Delivery Available": "Delivery Available",
    "Pickup Available": "Pickup Available",
    "Online / Remote Service": "Online / Remote Service",

    // Categories page
    "All Categories Heading": "All Categories",
    "Browse USDT-friendly businesses in Dubai by category.": "Browse USDT-friendly businesses in Dubai by category.",

    // Listing detail
    "Back to listings": "Back to listings",
    "Price": "Price",
    "Approx.": "Approx.",
    "USDT Accepted Directly by Merchant": "USDT Accepted Directly by Merchant",
    "Arrange payment directly with the merchant.": "Arrange payment directly with the merchant.",
    "Supported networks:": "Supported networks:",
    "CONTACT MERCHANT": "CONTACT MERCHANT",
    "Contact Merchant": "Contact Merchant",
    "WhatsApp": "WhatsApp",
    "Call": "Call",
    "Email": "Email",
    "Send a message": "Send a message",
    "Your Name": "Your Name",
    "Email Address": "Email Address",
    "Phone (Optional)": "Phone (Optional)",
    "Message": "Message",
    "Send Inquiry": "Send Inquiry",
    "Sending...": "Sending...",
    "Similar Listings": "Similar Listings",
    "Description": "Description",
    "DEMO LISTING — This is a sample listing for demonstration purposes.": "DEMO LISTING — This is a sample listing for demonstration purposes.",
    "Listing not found": "Listing not found",
    "The listing you are looking for does not exist or has been removed.": "The listing you are looking for does not exist or has been removed.",
    "Back to Explore": "Back to Explore",
    "No image available": "No image available",
    "Contact": "Contact",
    "USDT Accepted Directly": "USDT Accepted Directly",
    "Contact the merchant to confirm payment terms.": "Contact the merchant to confirm payment terms.",

    // Application form
    "Merchant Application": "Merchant Application",
    "Application Submitted!": "Application Submitted!",
    "Application reference": "Application reference",
    "Thank you for applying to join Souq Crypto. Our team will review your application and contact you within 24–48 hours.": "Thank you for applying to join Souq Crypto. Our team will review your application and contact you within 24–48 hours.",
    "Return to Homepage": "Return to Homepage",
    "Business Details": "Business Details",
    "Contact Information": "Contact Information",
    "Payment Details": "Payment Details",
    "Plan & Verification": "Plan & Verification",
    "Business Name": "Business Name",
    "Contact Person": "Contact Person",
    "Business Description": "Business Description",
    "Describe what you offer...": "Describe what you offer...",
    "Phone Number": "Phone Number",
    "WhatsApp Number": "WhatsApp Number",
    "Highly recommended for fast conversions.": "Highly recommended for fast conversions.",
    "Website or Instagram Link": "Website or Instagram Link",
    "Do you accept USDT directly?": "Do you accept USDT directly?",
    "Yes, we accept USDT to our own wallet": "Yes, we accept USDT to our own wallet",
    "No, not currently.": "No, not currently.",
    "Supported Networks": "Supported Networks",
    "Which networks do you accept USDT on?": "Which networks do you accept USDT on?",
    "Select Plan": "Select Plan",
    "Standard visibility, up to 5 listings.": "Standard visibility, up to 5 listings.",
    "Maximum visibility, featured badges, unlimited listings.": "Maximum visibility, featured badges, unlimited listings.",
    "Trade Licence Number (Optional)": "Trade Licence Number (Optional)",
    "For internal verification only": "For internal verification only",
    "Private — not displayed publicly": "Private — not displayed publicly",
    "I accept the terms and conditions": "I accept the terms and conditions",
    "By submitting this application, you agree to our merchant terms. Souq Crypto is a discovery platform and does not handle any funds.": "By submitting this application, you agree to our merchant terms. Souq Crypto is a discovery platform and does not handle any funds.",
    "Submit Application": "Submit Application",
    "Submitting Application...": "Submitting Application...",
    "Submission failed": "Submission failed",
    "There was an error submitting your application. Please try again.": "There was an error submitting your application. Please try again.",

    // Footer
    "Platform": "Platform",
    "Contact & Legal": "Contact & Legal",
    "Privacy Policy": "Privacy Policy",
    "Terms of Service": "Terms of Service",
    "Admin Panel": "Admin Panel",
    "All rights reserved.": "All rights reserved.",

    // Admin
    "Admin Login": "Admin Login",
    "Admin Password": "Admin Password",
    "Deals & Leads": "Deals & Leads",
    "Login": "Login",
    "Logout": "Logout",
    "Applications": "Applications",
    "Listings": "Listings",
    "Inquiries": "Inquiries",
    "Loading...": "Loading...",

    // Admin table columns
    "Business": "Business",
    "Category/Area": "Category / Area",
    "Plan": "Plan",
    "Status": "Status",
    "Actions": "Actions",
    "Image": "Image",
    "Title": "Title",
    "Merchant": "Merchant",
    "Listing": "Listing",
    "Published": "Published",
    "Date": "Date",
    "Customer Info": "Customer Info",
    // Admin application statuses & actions
    "approved": "Approved",
    "rejected": "Rejected",
    "pending": "Pending",
    "Approve": "Approve",
    "Reject": "Reject",
    "Application approved": "Application approved",
    "Application rejected": "Application rejected",
    "Application pending": "Application set to pending",

    // Admin empty states
    "No applications found.": "No applications found.",
    "No listings found.": "No listings found.",
    "No inquiries found.": "No inquiries found.",

    // Admin actions
    "Listing updated": "Listing updated",
    "Listing deleted": "Listing deleted",
    "Delete listing": "Delete listing",
    "Are you sure you want to delete this listing?": "Are you sure you want to delete this listing?",

    // Pagination
    "Page": "Page",
    "of": "of",

    // Product / Service
    "AED": "AED",
    "Product": "Product",
    "Service": "Service",
    "Featured Partner badge": "Featured Partner",

    // Category names (used in form selectors)
    "Electronics": "Electronics",
    "Cars & Rentals": "Cars & Rentals",
    "Beauty & Barber": "Beauty & Barber",
    "Medical & Wellness": "Medical & Wellness",
    "Restaurants & Cafés": "Restaurants & Cafés",
    "Hotels & Travel": "Hotels & Travel",
    "Professional Services": "Professional Services",
    "Shopping & Lifestyle": "Shopping & Lifestyle",

    // Dubai area names (used in form selectors)
    "Downtown Dubai": "Downtown Dubai",
    "Business Bay": "Business Bay",
    "Dubai Marina": "Dubai Marina",
    "Meydan": "Meydan",
    "Jumeirah": "Jumeirah",
    "Al Barsha": "Al Barsha",
    "Palm Jumeirah": "Palm Jumeirah",

    // Form placeholders and labels
    "Select category": "Select category",
    "Select area": "Select area",
    "Free": "Free",
    "AED 499/mo": "AED 499/mo",

    // Listing detail
    "Error": "Error",
    "Failed to send inquiry. Please try again.": "Failed to send inquiry. Please try again.",
    "Hours:": "Hours:",
    "ERC20, TRC20": "ERC20, TRC20",

    // Demo badge
    "Demo": "Demo",

    // Request This Deal flow
    "Request This Deal": "Request This Deal",
    "Submit your details to receive the merchant's contact info and a unique reference code.": "Submit your details to receive the merchant's contact info and a unique reference code.",
    "WhatsApp Number (e.g. +971501234567)": "WhatsApp Number (e.g. +971501234567)",
    "Email (optional)": "Email (optional)",
    "I agree that Souq Crypto may share my name and WhatsApp with the merchant to connect me with this listing. Souq Crypto does not handle funds.": "I agree that Souq Crypto may share my name and WhatsApp with the merchant to connect me with this listing. Souq Crypto does not handle funds.",
    "Get Contact Details": "Get Contact Details",
    "Cancel": "Cancel",
    "Your reference code": "Your reference code",
    "Contact Merchant Now": "Contact Merchant Now",
    "WhatsApp message (copy & send)": "WhatsApp message (copy & send)",
    "Copy message": "Copy message",
    "Copied!": "Copied!",
    "Submitting...": "Submitting...",
    "Something went wrong. Please try again.": "Something went wrong. Please try again.",
    "Souq Crypto introduces customers to independent merchants but does not receive, hold, transfer, or process customer funds.": "Souq Crypto introduces customers to independent merchants but does not receive, hold, transfer, or process customer funds.",
  },
  ar: {
    // Nav / Header
    "Explore": "استكشاف",
    "Products": "المنتجات",
    "Services": "الخدمات",
    "For Businesses": "للشركات",
    "About": "حول",
    "List Your Business": "أضف نشاطك التجاري",
    "Home": "الرئيسية",
    "Deals": "العروض",
    "FAQ": "الأسئلة الشائعة",
    "Dubai guide": "دليل دبي",
    "Toggle language": "تبديل اللغة",
    "English": "الإنجليزية",
    "All Listings": "كل الإعلانات",
    "No listings available for this merchant yet.": "لا توجد إعلانات متاحة لهذا التاجر بعد.",
    "Opening Hours": "ساعات العمل",
    "Service Methods": "طرق الخدمة",
    "USDT Networks": "شبكات USDT",
    "Notified": "تم الإشعار",
    "Webhook Pending": "الإشعار قيد الانتظار",

    // Hero
    "Discover USDT-Friendly Businesses in Dubai": "اكتشف الأنشطة التجارية الموافقة لـ USDT في دبي",
    "Discover where to spend USDT in Dubai": "اكتشف أين تنفق USDT في دبي",
    "Browse products and services from independent Dubai merchants that state they accept USDT. Contact each merchant directly to confirm availability, pricing, and payment terms.": "تصفح المنتجات والخدمات من التجار المستقلين في دبي الذين يذكرون قبول USDT. تواصل مع كل تاجر مباشرة للتأكد من التوافر والأسعار وشروط الدفع.",
    "Search products, services, or businesses": "ابحث عن منتجات أو خدمات أو أعمال تجارية",
    "All Areas": "جميع المناطق",
    "Explore Dubai": "استكشف دبي",
    "Direct Peer-to-Peer": "تعاملات مباشرة بين الطرفين",
    "No Wallets Connected": "لا محافظ متصلة",
    "Independent Merchants": "تجار مستقلون",

    // Categories section
    "Categories": "الفئات",
    "View all": "عرض الكل",
    "listings": "إعلانات",

    // Featured listings
    "Featured": "مميز",
    "Featured Listings": "الإعلانات المميزة",
    "Browse all featured": "تصفح جميع المميزة",
    "USDT Accepted": "يُقبل USDT",
    "View Details": "عرض التفاصيل",
    "Contact for price": "تواصل لمعرفة السعر",

    // How It Works
    "How It Works": "كيف يعمل الموقع؟",
    "Simple steps to find USDT-friendly businesses in Dubai.": "طريقة بسيطة لاكتشاف الأنشطة التجارية التي تتعامل بعملة USDT في دبي.",
    "Discover": "اكتشف",
    "Find listed products and services from Dubai merchants.": "ابحث عن المنتجات والخدمات التي تقدمها الأنشطة التجارية المدرجة في دبي.",
    "Contact the Merchant": "تواصل مع التاجر",
    "Contact directly via WhatsApp, phone, or email to confirm details and availability.": "تواصل مباشرة عبر واتساب أو الهاتف أو البريد الإلكتروني للتأكد من التفاصيل والتوافر.",
    "Arrange Payment Directly": "اتفق على طريقة الدفع",
    "Agree with the merchant on price, transfer network, and payment terms. Souq Crypto does not receive any funds.": "اتفق مع التاجر مباشرة على السعر وشبكة التحويل وشروط الدفع. لا يستلم Souq Crypto أي أموال.",

    // Regional Expansion Teaser
    "OUR EXPANSION ROADMAP": "خطة التوسع",
    "Dubai First. Three Major Cities Next.": "البداية من دبي، وثلاث مدن رئيسية هي الخطوة التالية.",
    "Dubai is our first-year launch market. As our merchant community grows, Souq Crypto plans to expand into three major regional cities. The next destinations will be revealed as partnerships are confirmed.": "دبي هي سوق الانطلاق خلال السنة الأولى. ومع نمو شبكة التجار، يخطط Souq Crypto للتوسع إلى ثلاث مدن إقليمية رئيسية، وسيتم الكشف عنها مع تأكيد الشراكات.",
    "YEAR 1 — LAUNCH MARKET": "السنة الأولى — سوق الانطلاق",
    "Dubai": "دبي",
    "Building Dubai's focused directory for discovering USDT-friendly products, services, and businesses.": "نبني دليلاً متخصصاً يساعد المستخدمين على اكتشاف المنتجات والخدمات والأنشطة التجارية التي تتعامل بعملة USDT في دبي.",
    "NEXT CITY": "المدينة التالية",
    "TO BE REVEALED": "سيتم الكشف عنها قريباً",
    "A major regional market is planned as part of the next expansion phase.": "سوق إقليمي رئيسي مخطط له ضمن مرحلة التوسع التالية.",
    "Follow our journey. The next cities will be announced soon.": "تابع رحلتنا، وسيتم الإعلان عن المدن التالية قريباً.",

    // Homepage CTA
    "Are you a Dubai merchant?": "هل أنت تاجر في دبي؟",
    "List your Dubai business on Souq Crypto and connect with customers searching for USDT-friendly merchants.": "أضف نشاطك التجاري في دبي على سوق كريبتو وتواصل مع العملاء الباحثين عن التجار الذين يقبلون USDT.",
    "List Your Business Now": "أضف نشاطك التجاري الآن",
    "Learn More": "اعرف المزيد",

    // Disclaimer
    "Souq Crypto is an independent business directory. We do not exchange, receive, hold, transfer, or process cryptocurrency or customer funds. Customers and merchants arrange transactions independently and are responsible for confirming all payment terms.": "سوق كريبتو دليل تجاري مستقل. نحن لا نتبادل أو نستلم أو نحتفظ أو ننقل أو نعالج أي عملات مشفرة أو أموال العملاء. يتولى العملاء والتجار ترتيب المعاملات بشكل مستقل وهم مسؤولون عن تأكيد جميع شروط الدفع.",
    "Disclaimer: Souq Crypto is an independent business directory.": "إخلاء مسؤولية: سوق كريبتو دليل تجاري مستقل.",
    "Souq Crypto does not hold or process any funds; all payments are between buyer and merchant.": "سوق كريبتو لا يحتفظ أو يعالج أي أموال؛ جميع المدفوعات تتم بين المشتري والتاجر.",

    // For Businesses page
    "Reach Customers Who Prefer USDT": "الوصول إلى العملاء الذين يفضلون USDT",
    "List your Dubai business on Souq Crypto and connect with customers searching for products and services from USDT-friendly merchants.": "أضف نشاطك التجاري في دبي على سوق كريبتو وتواصل مع العملاء الباحثين عن منتجات وخدمات من التجار الموافقين لـ USDT.",
    "Apply to List Your Business": "تقدم لإضافة نشاطك التجاري",
    "Why list on Souq Crypto?": "لماذا تُدرج نشاطك في سوق كريبتو؟",
    "Connect with customers searching for USDT-friendly businesses in Dubai.": "تواصل مع العملاء الباحثين عن أنشطة تجارية تقبل USDT في دبي.",
    "Direct Customer Relationships": "علاقات مباشرة مع العملاء",
    "Buyers contact you directly via WhatsApp, phone, or email.": "يتواصل المشترون معك مباشرة عبر واتساب أو الهاتف أو البريد الإلكتروني.",
    "No Transaction Fees": "بدون رسوم معاملات",
    "Category & Keyword Search": "البحث بالفئة والكلمة المفتاحية",
    "Buyers filter by category, area, product type, and keywords to find exactly what they need — surfacing your listings to the right people.": "يفلتر المشترون حسب الفئة والمنطقة ونوع المنتج والكلمات المفتاحية للعثور على ما يحتاجونه — مما يُظهر قوائمك للأشخاص المناسبين.",
    "We do not process payments or take transaction fees. All funds go directly between buyer and merchant.": "نحن لا نعالج المدفوعات أو نأخذ رسوم معاملات. تتم جميع المعاملات مباشرة بين المشتري والتاجر.",
    "Increased Visibility": "زيادة الظهور",
    "Stand out in a focused directory for USDT-friendly businesses in Dubai.": "تميّز في دليل مخصص للأنشطة التجارية الصديقة لـ USDT في دبي.",
    "Listing Options": "خيارات الإدراج",
    "Choose the option that fits your business.": "اختر الخيار الذي يناسب نشاطك التجاري.",
    "Basic Listing": "قائمة أساسية",
    "Free basic listings are available during the launch period.": "الإدراج الأساسي متاح مجاناً خلال فترة الإطلاق.",
    "Standard merchant profile": "ملف تاجر أساسي",
    "Up to 5 product/service listings": "حتى 5 إعلانات منتجات/خدمات",
    "Direct WhatsApp & Email leads": "عملاء مباشرون عبر واتساب والبريد الإلكتروني",
    "Standard search visibility": "ظهور قياسي في البحث",
    "Get Listed Free": "أضف نشاطك مجاناً",
    "Featured Partner": "شريك مميز",
    "Maximum visibility for established businesses.": "أقصى قدر من الظهور للأعمال التجارية الراسخة.",
    "Everything in Basic": "كل ما في الباقة الأساسية",
    "Unlimited listings": "إعلانات غير محدودة",
    "Homepage placement": "موضع على الصفحة الرئيسية",
    "Featured badge on all listings": "شارة مميزة على جميع الإعلانات",
    "Priority in search results": "أولوية في نتائج البحث",
    "Apply for Early Access": "التقديم للوصول المبكر",
    "No payment required today. We will contact you.": "لا يُطلب دفع اليوم. سنتواصل معك.",
    "How listing works": "كيف يعمل الإدراج",
    "Apply": "التقديم",
    "Fill out the application form with your business details.": "أكمل نموذج الطلب بتفاصيل نشاطك التجاري.",
    "Review": "المراجعة",
    "Our team reviews your application and may be in touch with questions.": "يراجع فريقنا طلبك وقد يتواصل معك للاستفسار.",
    "Go Live": "الإطلاق",
    "Your listings go live. Buyers contact you directly to arrange USDT payment.": "تنشر إعلاناتك. يتواصل المشترون معك مباشرة لترتيب الدفع بـ USDT.",
    "Recommended": "موصى به",
    "during launch": "خلال فترة الإطلاق",

    // Explore page
    "Filters": "الفلاتر",
    "Search": "بحث",
    "Keywords...": "كلمات مفتاحية...",
    "Type": "النوع",
    "All": "الكل",
    "Category": "الفئة",
    "All Categories": "جميع الفئات",
    "Dubai Area": "منطقة دبي",
    "Premium Only": "المميزة فقط",
    "Clear Filters": "مسح الفلاتر",
    "Showing results": "عرض",
    "Showing": "عرض",
    "results": "نتائج",
    "Newest": "الأحدث",
    "Name (A-Z)": "الاسم (أ-ي)",
    "Sort by": "ترتيب حسب",
    "No results found": "لم يتم العثور على نتائج",
    "Try adjusting your filters to find what you're looking for.": "حاول تعديل الفلاتر للعثور على ما تبحث عنه.",
    "Ask price": "السعر عند الطلب",
    "How do you serve customers?": "كيف تقدم خدماتك للعملاء؟",
    "Select at least one service method": "اختر طريقة خدمة واحدة على الأقل",
    "All service methods": "جميع طرق الخدمة",
    "Service methods": "طرق الخدمة",
    "Edit service methods": "تعديل طرق الخدمة",
    "Service at Merchant Location": "الخدمة في موقع التاجر",
    "Service at Customer Location / Home Service": "الخدمة في موقع العميل / خدمة منزلية",
    "Physical Goods": "سلع مادية",
    "Delivery Available": "التوصيل متاح",
    "Pickup Available": "الاستلام متاح",
    "Online / Remote Service": "خدمة عبر الإنترنت / عن بعد",

    // Categories page
    "All Categories Heading": "جميع الفئات",
    "Browse USDT-friendly businesses in Dubai by category.": "تصفح أنشطة دبي التجارية الصديقة لـ USDT حسب الفئة.",

    // Listing detail
    "Back to listings": "العودة إلى الإعلانات",
    "Price": "السعر",
    "Approx.": "تقريباً",
    "USDT Accepted Directly by Merchant": "يُقبل USDT مباشرة من قبل التاجر",
    "Arrange payment directly with the merchant.": "رتّب الدفع مباشرة مع التاجر.",
    "Supported networks:": "الشبكات المدعومة:",
    "CONTACT MERCHANT": "تواصل مع التاجر",
    "Contact Merchant": "تواصل مع التاجر",
    "WhatsApp": "واتساب",
    "Call": "اتصال",
    "Email": "بريد إلكتروني",
    "Send a message": "أرسل رسالة",
    "Your Name": "اسمك",
    "Email Address": "عنوان البريد الإلكتروني",
    "Phone (Optional)": "الهاتف (اختياري)",
    "Message": "رسالة",
    "Send Inquiry": "إرسال استفسار",
    "Sending...": "جاري الإرسال...",
    "Similar Listings": "إعلانات مشابهة",
    "Description": "الوصف",
    "DEMO LISTING — This is a sample listing for demonstration purposes.": "إعلان تجريبي — هذا إعلان نموذجي لأغراض العرض.",
    "Listing not found": "الإعلان غير موجود",
    "The listing you are looking for does not exist or has been removed.": "الإعلان الذي تبحث عنه غير موجود أو تم حذفه.",
    "Back to Explore": "العودة إلى الاستكشاف",
    "No image available": "لا توجد صورة",
    "Contact": "تواصل",
    "USDT Accepted Directly": "يُقبل USDT مباشرة",
    "Contact the merchant to confirm payment terms.": "تواصل مع التاجر للتأكد من شروط الدفع.",

    // Application form
    "Merchant Application": "طلب تاجر",
    "Application Submitted!": "تم تقديم الطلب!",
    "Application reference": "رقم مرجع الطلب",
    "Thank you for applying to join Souq Crypto. Our team will review your application and contact you within 24–48 hours.": "شكراً لتقديمك طلب الانضمام إلى سوق كريبتو. سيراجع فريقنا طلبك ويتواصل معك خلال 24-48 ساعة.",
    "Return to Homepage": "العودة إلى الصفحة الرئيسية",
    "Business Details": "تفاصيل النشاط التجاري",
    "Contact Information": "معلومات التواصل",
    "Payment Details": "تفاصيل الدفع",
    "Plan & Verification": "الخطة والتحقق",
    "Business Name": "اسم النشاط التجاري",
    "Contact Person": "الشخص المسؤول",
    "Business Description": "وصف النشاط التجاري",
    "Describe what you offer...": "صف ما تقدمه...",
    "Phone Number": "رقم الهاتف",
    "WhatsApp Number": "رقم الواتساب",
    "Highly recommended for fast conversions.": "موصى به للحصول على استجابة أسرع.",
    "Website or Instagram Link": "رابط الموقع أو إنستغرام",
    "Do you accept USDT directly?": "هل تقبلون USDT مباشرة؟",
    "Yes, we accept USDT to our own wallet": "نعم، نقبل USDT في محفظتنا الخاصة",
    "No, not currently.": "لا، ليس حالياً.",
    "Supported Networks": "الشبكات المدعومة",
    "Which networks do you accept USDT on?": "ما الشبكات التي تقبلون USDT عليها؟",
    "Select Plan": "اختر الخطة",
    "Standard visibility, up to 5 listings.": "ظهور قياسي، حتى 5 إعلانات.",
    "Maximum visibility, featured badges, unlimited listings.": "أقصى قدر من الظهور، شارات مميزة، إعلانات غير محدودة.",
    "Trade Licence Number (Optional)": "رقم الرخصة التجارية (اختياري)",
    "For internal verification only": "للاستخدام الداخلي فقط",
    "Private — not displayed publicly": "خاص — لا يُعرض للعموم",
    "I accept the terms and conditions": "أوافق على الشروط والأحكام",
    "By submitting this application, you agree to our merchant terms. Souq Crypto is a discovery platform and does not handle any funds.": "بتقديم هذا الطلب، توافق على شروط التاجر لدينا. سوق كريبتو منصة استكشاف ولا تتعامل مع أي أموال.",
    "Submit Application": "تقديم الطلب",
    "Submitting Application...": "جاري تقديم الطلب...",
    "Submission failed": "فشل التقديم",
    "There was an error submitting your application. Please try again.": "حدث خطأ أثناء تقديم الطلب. يرجى المحاولة مرة أخرى.",

    // Footer
    "Platform": "المنصة",
    "Contact & Legal": "التواصل والقانونية",
    "Privacy Policy": "سياسة الخصوصية",
    "Terms of Service": "شروط الخدمة",
    "Admin Panel": "لوحة تحكم المسؤول",
    "All rights reserved.": "جميع الحقوق محفوظة.",

    // Admin
    "Admin Login": "تسجيل دخول الإدارة",
    "Admin Password": "كلمة مرور الإدارة",
    "Deals & Leads": "الصفقات والعملاء المحتملون",
    "Login": "تسجيل الدخول",
    "Logout": "تسجيل الخروج",
    "Applications": "الطلبات",
    "Listings": "الإعلانات",
    "Inquiries": "الاستفسارات",
    "Loading...": "جاري التحميل...",

    // Admin table columns
    "Business": "النشاط التجاري",
    "Category/Area": "الفئة / المنطقة",
    "Plan": "الخطة",
    "Status": "الحالة",
    "Actions": "الإجراءات",
    "Image": "الصورة",
    "Title": "العنوان",
    "Merchant": "التاجر",
    "Listing": "الإعلان",
    "Published": "منشور",
    "Date": "التاريخ",
    "Customer Info": "بيانات العميل",
    // Admin application statuses & actions
    "approved": "موافق عليه",
    "rejected": "مرفوض",
    "pending": "قيد المراجعة",
    "Approve": "موافقة",
    "Reject": "رفض",
    "Application approved": "تمت الموافقة على الطلب",
    "Application rejected": "تم رفض الطلب",
    "Application pending": "تم تحويل الطلب للمراجعة",

    // Admin empty states
    "No applications found.": "لا توجد طلبات.",
    "No listings found.": "لا توجد إعلانات.",
    "No inquiries found.": "لا توجد استفسارات.",

    // Admin actions
    "Listing updated": "تم تحديث الإعلان",
    "Listing deleted": "تم حذف الإعلان",
    "Delete listing": "حذف الإعلان",
    "Are you sure you want to delete this listing?": "هل أنت متأكد من حذف هذا الإعلان؟",

    // Pagination
    "Page": "صفحة",
    "of": "من",

    // Product / Service
    "AED": "درهم",
    "Product": "منتج",
    "Service": "خدمة",
    "Featured Partner badge": "شريك مميز",

    // Category names (used in form selectors)
    "Electronics": "إلكترونيات",
    "Cars & Rentals": "سيارات وتأجير",
    "Beauty & Barber": "تجميل وحلاقة",
    "Medical & Wellness": "طبي وعافية",
    "Restaurants & Cafés": "مطاعم وكافيهات",
    "Hotels & Travel": "فنادق وسفر",
    "Professional Services": "خدمات مهنية",
    "Shopping & Lifestyle": "تسوق وأسلوب حياة",

    // Dubai area names
    "Downtown Dubai": "وسط مدينة دبي",
    "Business Bay": "الخليج التجاري",
    "Dubai Marina": "مرسى دبي",
    "Meydan": "ميدان",
    "Jumeirah": "الجميرا",
    "Al Barsha": "البرشاء",
    "Palm Jumeirah": "نخلة الجميرا",

    // Form placeholders and labels
    "Select category": "اختر الفئة",
    "Select area": "اختر المنطقة",
    "Free": "مجاناً",
    "AED 499/mo": "٤٩٩ درهم/شهر",

    // Listing detail
    "Error": "خطأ",
    "Failed to send inquiry. Please try again.": "فشل إرسال الاستفسار. يرجى المحاولة مرة أخرى.",
    "Hours:": "ساعات العمل:",
    "ERC20, TRC20": "ERC20، TRC20",

    // Demo badge
    "Demo": "تجريبي",

    // Request This Deal flow
    "Request This Deal": "طلب هذه الصفقة",
    "Submit your details to receive the merchant's contact info and a unique reference code.": "أدخل بياناتك للحصول على معلومات تواصل التاجر ورمز مرجعي فريد.",
    "WhatsApp Number (e.g. +971501234567)": "رقم الواتساب (مثال: +971501234567)",
    "Email (optional)": "البريد الإلكتروني (اختياري)",
    "I agree that Souq Crypto may share my name and WhatsApp with the merchant to connect me with this listing. Souq Crypto does not handle funds.": "أوافق على أن يشارك سوق كريبتو اسمي ورقم واتساب مع التاجر للتواصل بشأن هذا الإعلان. سوق كريبتو لا يتعامل بالأموال.",
    "Get Contact Details": "الحصول على بيانات التواصل",
    "Cancel": "إلغاء",
    "Your reference code": "رمزك المرجعي",
    "Contact Merchant Now": "تواصل مع التاجر الآن",
    "WhatsApp message (copy & send)": "رسالة الواتساب (انسخ وأرسل)",
    "Copy message": "نسخ الرسالة",
    "Copied!": "تم النسخ!",
    "Submitting...": "جاري الإرسال...",
    "Something went wrong. Please try again.": "حدث خطأ ما. يرجى المحاولة مرة أخرى.",
    "Souq Crypto introduces customers to independent merchants but does not receive, hold, transfer, or process customer funds.": "يربط سوق كريبتو العملاء بالتجار المستقلين دون أن يستلم أو يحتفظ أو ينقل أو يعالج أموال العملاء.",
  }
} as const;

export type TranslationKey = keyof typeof translations.en | (string & {});
export type Language = ActiveLocale;

export function getLocaleCoverageReport() {
  const englishKeys = Object.keys(translations.en) as Array<keyof typeof translations.en>;
  const arabicKeys = Object.keys(translations.ar);
  const allowedIdentical = /^(USDT|AED|TRC20|ERC20|WhatsApp|Souq Crypto)$/;
  const missingArabic = englishKeys.filter((key) => !arabicKeys.includes(key));
  const identicalArabic = englishKeys.filter((key) => translations.en[key] === (translations.ar as Record<string, string>)[key] && !allowedIdentical.test(translations.en[key]));
  return {
    activeLocales,
    preparedLocales: preparedLocales.map((locale) => ({ locale, ...localeConfig[locale], humanReviewRequired: true })),
    englishKeyCount: englishKeys.length,
    arabicKeyCount: arabicKeys.length,
    missingArabic,
    identicalArabic,
    ready: missingArabic.length === 0 && identicalArabic.length === 0,
  };
}
